import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Stack,
  Typography,
  Card,
  Divider,
  IconButton,
  Avatar,
} from '@mui/joy';
import { marked } from 'marked';
import { ProjectSection, ContentBlock } from 'assets/Projects';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { HiOutlineDocumentText } from 'react-icons/hi';
import { useMobileMode } from 'components/Responsive';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

// Component for rendering mobile images at 1/4 size with slider
interface MobileImageBlockProps {
  urls: string[];
  captions?: string[];
}

function MobileImageBlock({ urls, captions }: MobileImageBlockProps) {
  const mobile = useMobileMode();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Embla carousel setup with same configuration as ImageBlock
  const duplications = 20;
  const infiniteUrls = Array(duplications).fill(urls).flat();

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      slidesToScroll: 'auto',
      align: 'start',
      startIndex: Math.floor(infiniteUrls.length / 2),
    },
    [Autoplay({ delay: 3000, stopOnInteraction: false, stopOnMouseEnter: false })],
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCurrentIndex(emblaApi.selectedScrollSnap() % urls.length);
  }, [emblaApi, urls.length]);

  useEffect(() => {
    if (emblaApi) {
      emblaApi.scrollTo(Math.floor(infiniteUrls.length / 2), true);
      onSelect();
      emblaApi.on('select', onSelect);
      emblaApi.on('reInit', onSelect);
    }
  }, [emblaApi, infiniteUrls.length, onSelect]);

  const scrollTo = useCallback((index: number) => {
    if (emblaApi) emblaApi.scrollTo(index);
  }, [emblaApi]);

  if (!urls || urls.length === 0) return null;

  // Single image
  if (urls.length === 1) {
    return (
      <Stack gap={0.5} marginY={2} alignItems={mobile ? 'stretch' : 'flex-start'}>
        <Box
          component="img"
          src={urls[0]}
          alt={captions?.[0] || 'Mobile image'}
          sx={{
            width: mobile ? '100%' : '25%',
            height: 'auto',
            display: 'block',
            borderRadius: '8px',
            border: '1px solid',
            borderColor: 'divider',
          }}
        />
        {captions?.[0] && (
          <Typography level="body3" textAlign="center" sx={{ fontStyle: 'italic' }}>
            {captions[0]}
          </Typography>
        )}
      </Stack>
    );
  }

  // Multiple images - carousel at 25% width on desktop, 100% on mobile
  return (
    <Stack gap={0.5} marginY={2}>
      <Box
        sx={{
          position: 'relative',
          overflow: '',
          width: mobile ? '100%' : '25%',
        }}
      >
        <Box ref={emblaRef} sx={{ overflow: '' }}>
          <Box
            sx={{
              display: 'flex',
              touchAction: 'pan-y pinch-zoom',
            }}
          >
            {infiniteUrls.map((url, index) => {
              const originalIndex = index % urls.length;
              return (
                <Box
                  key={`slide-${url}`}
                  sx={{
                    flex: '0 0 100%',
                    minWidth: 0,
                    paddingRight: 1,
                  }}
                >
                  <Card
                    variant="outlined"
                    sx={{
                      padding: 0,
                      overflow: '',
                    }}
                  >
                    <Box
                      component="img"
                      src={url}
                      alt={captions?.[originalIndex] || `Image ${originalIndex + 1}`}
                      sx={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                        userSelect: 'none',
                      }}
                    />
                  </Card>
                </Box>
              );
            })}
          </Box>
        </Box>
        {/* Navigation arrows - removed for mobile-images slider */}
        {/* Pagination dots */}
        <Stack
          direction="row"
          gap={0.5}
          sx={{
            position: 'absolute',
            bottom: '1rem',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            padding: '0.5rem',
            borderRadius: '20px',
            zIndex: 2,
          }}
        >
          {urls.map((url, index) => (
            <Box
              key={`dot-${url}`}
              onClick={() => scrollTo(index)}
              sx={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: currentIndex === index ? 'white' : 'rgba(255, 255, 255, 0.5)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                },
              }}
            />
          ))}
        </Stack>
      </Box>
      {captions?.[currentIndex] && (
        <Typography
          level="body3"
          textAlign="center"
          sx={{
            fontStyle: 'italic',
            width: mobile ? '100%' : '25%',
          }}
        >
          {captions[currentIndex]}
        </Typography>
      )}
    </Stack>
  );
}

// Component for rendering image(s) - single or slider based on array length
interface ImageBlockProps {
  urls: string[];
  captions?: string[];
}

function ImageBlock({ urls, captions }: ImageBlockProps) {
  const mobile = useMobileMode();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Embla needs LOTS of duplicate slides for smooth infinite loop
  // Especially with full-width (100%) slides
  // Duplicate enough times to ensure seamless looping
  const duplications = 20; // High number ensures no gaps
  const infiniteUrls = Array(duplications).fill(urls).flat();

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      slidesToScroll: 'auto',
      align: 'start',
      startIndex: Math.floor(infiniteUrls.length / 2),
    },
    [Autoplay({ delay: 3000, stopOnInteraction: false, stopOnMouseEnter: false })],
  );

  // Track current slide index
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCurrentIndex(emblaApi.selectedScrollSnap() % urls.length);
  }, [emblaApi, urls.length]);

  // Scroll to middle on mount to show images on both sides
  useEffect(() => {
    if (emblaApi) {
      emblaApi.scrollTo(Math.floor(infiniteUrls.length / 2), true);
      onSelect();
      emblaApi.on('select', onSelect);
      emblaApi.on('reInit', onSelect);
    }
  }, [emblaApi, infiniteUrls.length, onSelect]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback((index: number) => {
    if (emblaApi) emblaApi.scrollTo(index);
  }, [emblaApi]);

  if (!urls || urls.length === 0) return null;

  // Single image - simple display
  if (urls.length === 1) {
    return (
      <Stack gap={0.5} marginY={2}>
        <Box
          component="img"
          src={urls[0]}
          alt={captions?.[0] || 'Project image'}
          sx={{
            width: '100%',
            height: 'auto',
            display: 'block',
            borderRadius: '8px',
            border: '1px solid',
            borderColor: 'divider',
          }}
        />
        {captions?.[0] && (
          <Typography level="body3" textAlign="center" sx={{ fontStyle: 'italic' }}>
            {captions[0]}
          </Typography>
        )}
      </Stack>
    );
  }

  // Multiple images - Embla Carousel slider with autoplay
  return (
    <Stack gap={0.5} marginY={2}>
      <Box
        sx={{
          position: 'relative',
          overflow: '',
        }}
      >
        <Box ref={emblaRef} sx={{ overflow: '' }}>
          <Box
            sx={{
              display: 'flex',
              touchAction: 'pan-y pinch-zoom',
            }}
          >
            {infiniteUrls.map((url, index) => {
              const originalIndex = index % urls.length;
              return (
                <Box
                  key={`slide-${url}`}
                  sx={{
                    flex: '0 0 100%',
                    minWidth: 0,
                    paddingRight: 1,
                  }}
                >
                  <Card
                    variant="outlined"
                    sx={{
                      padding: 0,
                      overflow: '',
                    }}
                  >
                    <Box
                      component="img"
                      src={url}
                      alt={captions?.[originalIndex] || `Image ${originalIndex + 1}`}
                      sx={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                        userSelect: 'none',
                      }}
                    />
                  </Card>
                </Box>
              );
            })}
          </Box>
        </Box>
        {/* Navigation arrows - hidden on mobile */}
        {!mobile && (
          <>
            <IconButton
              variant="soft"
              size="sm"
              onClick={scrollPrev}
              sx={{
                position: 'absolute',
                left: '2rem',
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                color: 'white',
                zIndex: 2,
                minWidth: '32px',
                minHeight: '32px',
                borderRadius: '100%',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                },
                '&:active': {
                  transform: 'translateY(-50%) scale(0.9)',
                },
              }}
            >
              <FiChevronLeft size={16} />
            </IconButton>
            <IconButton
              variant="soft"
              size="sm"
              onClick={scrollNext}
              sx={{
                position: 'absolute',
                right: '2rem',
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                color: 'white',
                zIndex: 2,
                minWidth: '32px',
                minHeight: '32px',
                borderRadius: '100%',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                },
                '&:active': {
                  transform: 'translateY(-50%) scale(0.9)',
                },
              }}
            >
              <FiChevronRight size={16} />
            </IconButton>
          </>
        )}
        {/* Pagination dots */}
        <Stack
          direction="row"
          gap={0.5}
          sx={{
            position: 'absolute',
            bottom: '1rem',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            padding: '0.5rem',
            borderRadius: '20px',
            zIndex: 2,
          }}
        >
          {urls.map((url, index) => (
            <Box
              key={`dot-${url}`}
              onClick={() => scrollTo(index)}
              sx={{
                width: mobile ? '6px' : '8px',
                height: mobile ? '6px' : '8px',
                borderRadius: '50%',
                backgroundColor: currentIndex === index ? 'white' : 'rgba(255, 255, 255, 0.5)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                },
              }}
            />
          ))}
        </Stack>
      </Box>
      {captions?.[currentIndex] && (
        <Typography level="body3" textAlign="center" sx={{ fontStyle: 'italic' }}>
          {captions[currentIndex]}
        </Typography>
      )}
    </Stack>
  );
}

// Component for rendering video
interface VideoBlockProps {
  url: string;
  caption?: string;
}

function VideoBlock({ url, caption }: VideoBlockProps) {
  // Check if it's a YouTube or Vimeo URL and extract ID
  const getEmbedUrl = (videoUrl: string): string => {
    // YouTube
    const youtubeMatch = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }
    // Vimeo
    const vimeoMatch = videoUrl.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }
    // Direct video URL
    return videoUrl;
  };

  const embedUrl = getEmbedUrl(url);
  const isEmbed = embedUrl.includes('youtube') || embedUrl.includes('vimeo');

  return (
    <Stack gap={0.5} marginY={2}>
      <Card
        variant="outlined"
        sx={{
          position: 'relative',
          width: '100%',
          padding: 0,
          overflow: 'hidden',
          aspectRatio: '16/9',
          maxHeight: '500px',
        }}
      >
        {isEmbed ? (
          <iframe
            src={embedUrl}
            title={caption || 'Project video'}
            width="100%"
            height="100%"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ border: 'none' }}
          />
        ) : (
          <video
            controls
            width="100%"
            height="100%"
            style={{ objectFit: 'cover' }}
          >
            <source src={url} type="video/mp4" />
            <track kind="captions" />
            Your browser does not support the video tag.
          </video>
        )}
      </Card>
      {caption && (
        <Typography level="body3" textAlign="center" sx={{ fontStyle: 'italic' }}>
          {caption}
        </Typography>
      )}
    </Stack>
  );
}

// Component for rendering code blocks
interface CodeBlockProps {
  language: string;
  code: string;
}

function CodeBlock({ language, code }: CodeBlockProps) {
  return (
    <Card
      variant="outlined"
      sx={(theme) => ({
        marginY: 2,
        padding: '1rem',
        backgroundColor: theme.palette.neutral[100],
        overflow: 'auto',
      })}
    >
      <pre style={{ margin: 0 }}>
        <code>
          {code}
        </code>
      </pre>
      <Typography
        level="body3"
        sx={{
          position: 'absolute',
          top: '0.5rem',
          right: '0.5rem',
          opacity: 0.6,
        }}
      >
        {language}
      </Typography>
    </Card>
  );
}

// Component for rendering markdown text
interface TextBlockProps {
  content: string;
}

function TextBlock({ content }: TextBlockProps) {
  return (
    <Box
      sx={(theme) => ({
        '& h1': { fontSize: '1.5rem', fontWeight: 700, marginY: 2 },
        '& h2': { fontSize: '1.3rem', fontWeight: 600, marginY: 1.5 },
        '& h3': { fontSize: '1.1rem', fontWeight: 600, marginY: 1 },
        '& h4': { fontSize: '1rem', fontWeight: 600, marginY: 0.5 },
        '& p': { marginY: 1, lineHeight: 1.7 },
        '& ul, & ol': {
          paddingLeft: '1.5rem',
          marginY: 0.5,
          '& li': {
            marginY: 0.25,
            lineHeight: 1.6,
            '& ul, & ol': {
              marginTop: 0.25,
            },
          },
        },
        '& strong': { fontWeight: 600 },
        '& code': {
          backgroundColor: theme.palette.neutral[100],
          padding: '2px 6px',
          borderRadius: '4px',
          fontSize: '0.9em',
          fontFamily: '"Fira Code", monospace',
        },
        '& pre': {
          backgroundColor: theme.palette.neutral[100],
          padding: '1rem',
          borderRadius: '8px',
          overflowX: 'auto',
          marginY: 1,
          '& code': {
            backgroundColor: 'transparent',
            padding: 0,
          },
        },
        '& blockquote': {
          borderLeft: `3px solid ${theme.palette.neutral[300]}`,
          paddingLeft: '1rem',
          marginY: 1,
          fontStyle: 'italic',
        },
        '& a': {
          color: theme.palette.primary[500],
          textDecoration: 'underline',
          '&:hover': {
            textDecoration: 'none',
          },
        },
        '& table': {
          width: '100%',
          borderCollapse: 'collapse',
          marginY: 1,
          '& th, & td': {
            border: `1px solid ${theme.palette.divider}`,
            padding: '0.5rem',
            textAlign: 'left',
          },
          '& th': {
            backgroundColor: theme.palette.neutral[100],
            fontWeight: 600,
          },
        },
      })}
      dangerouslySetInnerHTML={{
        __html: marked.parse(content || '', {
          gfm: true,
          breaks: true,
        }) as string,
      }}
    />
  );
}

// Main component for rendering a content block
interface ContentBlockRendererProps {
  block: ContentBlock;
}

function ContentBlockRenderer({ block }: ContentBlockRendererProps) {
  switch (block.type) {
    case 'text':
      return <TextBlock content={block.content} />;
    case 'images':
      return <ImageBlock urls={block.urls} captions={block.captions} />;
    case 'mobile-images':
      return <MobileImageBlock urls={block.urls} captions={block.captions} />;
    case 'video':
      return <VideoBlock url={block.url} caption={block.caption} />;
    case 'code':
      return <CodeBlock language={block.language} code={block.code} />;
    case 'divider':
      return <Divider sx={{ marginY: 3 }} />;
    default:
      return null;
  }
}

// Main component for rendering a project section
interface ProjectSectionRendererProps {
  section: ProjectSection;
  isLast?: boolean;
  index?: number;
}

export default function ProjectSectionRenderer({
  section,
  isLast,
  index = 0,
}: ProjectSectionRendererProps) {
  const mobile = useMobileMode();

  if (!section.blocks || section.blocks.length === 0) return null;

  // Define color progression for continuous gradient
  const colorPairs = [
    { from: 'info', to: 'warning' }, // index 0: blue → orange
    { from: 'warning', to: 'success' }, // index 1: orange → green
    { from: 'success', to: 'danger' }, // index 2: green → red
    { from: 'danger', to: 'neutral' }, // index 3: red → gray
  ];

  const colors = colorPairs[index % colorPairs.length];

  return (
    <Stack
      gap={mobile ? 2 : 1}
      alignItems={mobile ? 'center' : 'flex-start'}
      sx={(theme) => {
        const fromPalette = (theme.palette as unknown as Record<string, Record<string, string>>)[
          colors.from
        ];
        const toPalette = (theme.palette as unknown as Record<string, Record<string, string>>)[
          colors.to
        ];

        return {
          width: '100%',
          position: 'relative',
          '&::before': mobile ? {} : {
            content: '""',
            position: 'absolute',
            width: '2px',
            height: isLast ? 'calc(100% - 49px)' : 'calc(100% + 49px)',
            background: isLast
              ? `linear-gradient(to bottom, ${fromPalette[500]}, ${toPalette[500]} 50%, transparent)`
              : `linear-gradient(to bottom, ${fromPalette[500]}, ${toPalette[500]})`,
            left: '-49px',
            top: '0',
          },
        };
      }}
    >
      {mobile && (
        <Avatar
          alt={section.title}
          color={colors.from as 'primary' | 'warning' | 'success' | 'danger' | 'info' | 'neutral'}
          sx={(theme) => {
            const fromPalette = (
              theme.palette as unknown as Record<string, Record<string, string>>
            )[
              colors.from
            ];
            return {
              position: 'relative',
              border: 'none',
              outline: `2px solid ${fromPalette[500]}`,
              boxShadow: `0 0 40px 5px rgba(${fromPalette.mainChannel} / 0.4)`,
              overflow: 'visible',
              marginTop: '3rem',
              marginBottom: '1rem',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-5rem',
                height: '5rem',
                width: '2px',
                background: `linear-gradient(to bottom, transparent, ${fromPalette[500]})`,
              },
            };
          }}
        >
          <HiOutlineDocumentText />
        </Avatar>
      )}
      <Stack gap={1} width="100%">
        <Typography
          level="h2"
          fontWeight="lg"
          sx={{
            position: 'relative',
            textAlign: mobile ? 'center' : 'left',
          }}
        >
          {!mobile && (
            <Avatar
              alt={section.title}
              color={colors.from as 'primary' | 'warning' | 'success' | 'danger' | 'info' | 'neutral'}
              sx={(theme) => {
                const fromPalette = (
                  theme.palette as unknown as Record<string, Record<string, string>>
                )[
                  colors.from
                ];
                return {
                  position: 'absolute',
                  left: '-49px',
                  top: '0',
                  transform: 'translateX(-50%)',
                  border: 'none',
                  outline: `2px solid ${fromPalette[500]}`,
                  boxShadow: `0 0 40px 5px rgba(${fromPalette.mainChannel} / 0.4)`,
                };
              }}
            >
              <HiOutlineDocumentText />
            </Avatar>
          )}
          {section.title}
        </Typography>
        {section.blocks.map((block) => (
          <ContentBlockRenderer key={`block-${block.type}`} block={block} />
        ))}
      </Stack>
    </Stack>
  );
}
